// @catalog-federation — the organization's and the account's consents
// ============================================================================
// The TypeScript twin of libs/federation/Consents.cs. Three payloads an owner
// signs in the ceremony console (M6):
//
//   • `operator_binding_consent.v1` — the organization binds itself to an
//     operator code (D6). The `operator_binding` record carries exactly these
//     fields plus the signature.
//   • `account_origin_migration_consent.v1` — an account moves its origin
//     voluntarily (D20).
//   • `origin_migration_bulk_consent.v1` — the `from` operator's organization
//     agrees to a standing redirect of every account of its code (D20).
//
// Each embeds the signer's key, so verifying it never needs a CID lookup, and
// each carries a `type` that domain-separates the signature: the same
// `main_signing_key` signs many artefacts, and a consent must verify as
// nothing else. A consent file is the RFC 8785 bytes of
// `{ consent, org_signature }` (bulk, binding) or
// `{ consent, account_signature }` (account); canonical bytes only.
//
// The builders run the readers over what they build, so a malformed consent
// throws here instead of being signed and refused by `cl-root`.

import { canonicalize } from '@catalog-canonical-json';
import { type JsonObject, FederationFileError, StrictObject, parseCanonical, toBase64 } from './json';
import {
    type SignatureVerifier, type SigningProfile, HYBRID_PROFILE, profileJson, sameProfile, validatePublicKey,
} from './signatures';
import { isNaturalPersonCid, isOperatorCode, isOrganizationCid } from './records';

export const BINDING_CONSENT_TYPE = 'operator_binding_consent.v1';
export const ACCOUNT_MIGRATION_CONSENT_TYPE = 'account_origin_migration_consent.v1';
export const BULK_MIGRATION_CONSENT_TYPE = 'origin_migration_bulk_consent.v1';

/** What an organization signs to be bound to an operator code (D6). */
export interface BindingConsent {
    operatorId: string;
    /** The operator record's sequence of this binding; 1 is always the reservation. */
    sequence: number;
    orgCid: string;
    /** The organization's `main_signing_key`, 1984-byte hybrid. */
    orgPublicKey: Uint8Array;
    orgSigningProfile: SigningProfile;
    /** RFC 3339 UTC to the second. */
    effectiveFrom: string;
}

/** What an account signs to move its origin voluntarily (D20). */
export interface AccountMigrationConsent {
    /** A personal or an organization CID: a delegate never moves alone, an agent moves with its organization. */
    accountCid: string;
    sequence: number;
    fromOperatorId: string;
    toOperatorId: string;
    accountPublicKey: Uint8Array;
    accountSigningProfile: SigningProfile;
}

/** What the `from` operator's organization signs for a voluntary bulk migration (D20). */
export interface BulkMigrationConsent {
    fromOperatorId: string;
    toOperatorId: string;
    sequence: number;
    orgCid: string;
    orgPublicKey: Uint8Array;
    orgSigningProfile: SigningProfile;
}

// ── binding consent ─────────────────────────────────────────────────────

export function bindingConsentPayload(c: BindingConsent): JsonObject {
    const payload: JsonObject = {
        effective_from: c.effectiveFrom,
        operator_id: c.operatorId,
        org_cid: c.orgCid,
        org_public_key: toBase64(c.orgPublicKey),
        org_signing_profile: profileJson(c.orgSigningProfile),
        sequence: c.sequence,
        type: BINDING_CONSENT_TYPE,
    };
    readBindingConsent(StrictObject.of(payload, 'binding consent'));
    return payload;
}

/** The bytes the organization's `main_signing_key` signs. */
export function bindingConsentSigningBytes(c: BindingConsent): Uint8Array {
    return canonicalize(bindingConsentPayload(c));
}

/** The organization's signature, checked against the key the consent embeds. */
export function verifyBindingConsent(
    c: BindingConsent, orgSignature: Uint8Array, signatures: SignatureVerifier,
): Promise<boolean> {
    return signatures.verify(bindingConsentSigningBytes(c), orgSignature, c.orgPublicKey, c.orgSigningProfile);
}

/** The consent file `cl-root bind` takes. */
export function bindingConsentFile(c: BindingConsent, orgSignature: Uint8Array): Uint8Array {
    return canonicalize({ consent: bindingConsentPayload(c), org_signature: toBase64(orgSignature) });
}

/** A delivered, unsigned binding consent payload: canonical bytes, strict shape. */
export function readBindingConsentPayload(file: string | Uint8Array): BindingConsent {
    return readBindingConsent(StrictObject.of(parseCanonical(file, 'binding consent'), 'binding consent'));
}

/** Reads a consent file and checks its signature. Throws {@link FederationFileError}
 *  (`non_canonical`, `malformed`, `bad_signature`). */
export async function readBindingConsentFile(
    file: string | Uint8Array, signatures: SignatureVerifier,
): Promise<{ consent: BindingConsent; orgSignature: Uint8Array }> {
    const o = StrictObject.of(parseCanonical(file, 'binding consent file'), 'binding consent file');
    const consent = readBindingConsent(o.object('consent'));
    const orgSignature = o.base64('org_signature');
    o.end();
    if (!(await verifyBindingConsent(consent, orgSignature, signatures))) {
        throw new FederationFileError('bad_signature',
            'binding consent: the organization signature does not verify against the embedded key');
    }
    return { consent, orgSignature };
}

function readBindingConsent(p: StrictObject): BindingConsent {
    requireType(p, BINDING_CONSENT_TYPE);
    const operatorId = readOperatorId(p, 'operator_id');
    // Sequence 1 of the operator record is always the reservation (D6).
    const sequence = p.integer('sequence', 2);
    const orgCid = readOrganizationCid(p, 'org_cid');
    const orgPublicKey = p.base64('org_public_key');
    const orgSigningProfile = p.profile('org_signing_profile');
    requireHybridKey(p, 'org_public_key', orgSigningProfile, orgPublicKey);
    const effectiveFrom = p.timestamp('effective_from');
    p.end();
    return { operatorId, sequence, orgCid, orgPublicKey, orgSigningProfile, effectiveFrom };
}

// ── account migration consent ───────────────────────────────────────────

export function accountMigrationConsentPayload(c: AccountMigrationConsent): JsonObject {
    const payload: JsonObject = {
        account_cid: c.accountCid,
        account_public_key: toBase64(c.accountPublicKey),
        account_signing_profile: profileJson(c.accountSigningProfile),
        from_operator_id: c.fromOperatorId,
        sequence: c.sequence,
        to_operator_id: c.toOperatorId,
        type: ACCOUNT_MIGRATION_CONSENT_TYPE,
    };
    readAccountMigrationConsent(StrictObject.of(payload, 'migration consent'));
    return payload;
}

/** The bytes the account's `main_signing_key` signs. */
export function accountMigrationConsentSigningBytes(c: AccountMigrationConsent): Uint8Array {
    return canonicalize(accountMigrationConsentPayload(c));
}

export function verifyAccountMigrationConsent(
    c: AccountMigrationConsent, accountSignature: Uint8Array, signatures: SignatureVerifier,
): Promise<boolean> {
    return signatures.verify(
        accountMigrationConsentSigningBytes(c), accountSignature, c.accountPublicKey, c.accountSigningProfile);
}

/** The consent file `cl-root migrate` takes. */
export function accountMigrationConsentFile(c: AccountMigrationConsent, accountSignature: Uint8Array): Uint8Array {
    return canonicalize({ account_signature: toBase64(accountSignature), consent: accountMigrationConsentPayload(c) });
}

export function readAccountMigrationConsentPayload(file: string | Uint8Array): AccountMigrationConsent {
    return readAccountMigrationConsent(StrictObject.of(parseCanonical(file, 'migration consent'), 'migration consent'));
}

export async function readAccountMigrationConsentFile(
    file: string | Uint8Array, signatures: SignatureVerifier,
): Promise<{ consent: AccountMigrationConsent; accountSignature: Uint8Array }> {
    const o = StrictObject.of(parseCanonical(file, 'migration consent file'), 'migration consent file');
    const consent = readAccountMigrationConsent(o.object('consent'));
    const accountSignature = o.base64('account_signature');
    o.end();
    if (!(await verifyAccountMigrationConsent(consent, accountSignature, signatures))) {
        throw new FederationFileError('bad_signature',
            'migration consent: the account signature does not verify against the embedded key');
    }
    return { consent, accountSignature };
}

function readAccountMigrationConsent(p: StrictObject): AccountMigrationConsent {
    requireType(p, ACCOUNT_MIGRATION_CONSENT_TYPE);
    const accountCid = p.string('account_cid');
    if (!isNaturalPersonCid(accountCid) && !isOrganizationCid(accountCid)) {
        throw p.fail(`'account_cid' must be a personal or an organization CID; got '${accountCid}'`);
    }
    const sequence = p.integer('sequence', 1);
    const [fromOperatorId, toOperatorId] = readFromTo(p);
    const accountPublicKey = p.base64('account_public_key');
    const accountSigningProfile = p.profile('account_signing_profile');
    requireHybridKey(p, 'account_public_key', accountSigningProfile, accountPublicKey);
    p.end();
    return { accountCid, sequence, fromOperatorId, toOperatorId, accountPublicKey, accountSigningProfile };
}

// ── bulk migration consent ──────────────────────────────────────────────

export function bulkMigrationConsentPayload(c: BulkMigrationConsent): JsonObject {
    const payload: JsonObject = {
        from_operator_id: c.fromOperatorId,
        org_cid: c.orgCid,
        org_public_key: toBase64(c.orgPublicKey),
        org_signing_profile: profileJson(c.orgSigningProfile),
        sequence: c.sequence,
        to_operator_id: c.toOperatorId,
        type: BULK_MIGRATION_CONSENT_TYPE,
    };
    readBulkMigrationConsent(StrictObject.of(payload, 'bulk migration consent'));
    return payload;
}

/** The bytes the `from` organization's `main_signing_key` signs. */
export function bulkMigrationConsentSigningBytes(c: BulkMigrationConsent): Uint8Array {
    return canonicalize(bulkMigrationConsentPayload(c));
}

export function verifyBulkMigrationConsent(
    c: BulkMigrationConsent, orgSignature: Uint8Array, signatures: SignatureVerifier,
): Promise<boolean> {
    return signatures.verify(bulkMigrationConsentSigningBytes(c), orgSignature, c.orgPublicKey, c.orgSigningProfile);
}

/** The consent file `cl-root migrate-bulk` takes. */
export function bulkMigrationConsentFile(c: BulkMigrationConsent, orgSignature: Uint8Array): Uint8Array {
    return canonicalize({ consent: bulkMigrationConsentPayload(c), org_signature: toBase64(orgSignature) });
}

export function readBulkMigrationConsentPayload(file: string | Uint8Array): BulkMigrationConsent {
    return readBulkMigrationConsent(
        StrictObject.of(parseCanonical(file, 'bulk migration consent'), 'bulk migration consent'));
}

export async function readBulkMigrationConsentFile(
    file: string | Uint8Array, signatures: SignatureVerifier,
): Promise<{ consent: BulkMigrationConsent; orgSignature: Uint8Array }> {
    const o = StrictObject.of(parseCanonical(file, 'bulk migration consent file'), 'bulk migration consent file');
    const consent = readBulkMigrationConsent(o.object('consent'));
    const orgSignature = o.base64('org_signature');
    o.end();
    if (!(await verifyBulkMigrationConsent(consent, orgSignature, signatures))) {
        throw new FederationFileError('bad_signature',
            'bulk migration consent: the organization signature does not verify against the embedded key');
    }
    return { consent, orgSignature };
}

function readBulkMigrationConsent(p: StrictObject): BulkMigrationConsent {
    requireType(p, BULK_MIGRATION_CONSENT_TYPE);
    const [fromOperatorId, toOperatorId] = readFromTo(p);
    const sequence = p.integer('sequence', 1);
    const orgCid = readOrganizationCid(p, 'org_cid');
    const orgPublicKey = p.base64('org_public_key');
    const orgSigningProfile = p.profile('org_signing_profile');
    requireHybridKey(p, 'org_public_key', orgSigningProfile, orgPublicKey);
    p.end();
    return { fromOperatorId, toOperatorId, sequence, orgCid, orgPublicKey, orgSigningProfile };
}

// ── shared field rules ──────────────────────────────────────────────────

function requireType(p: StrictObject, type: string): void {
    if (p.string('type') !== type) throw p.fail(`'type' must be ${type}`);
}

function readOperatorId(p: StrictObject, field: string): string {
    const id = p.string(field);
    if (!isOperatorCode(id)) throw p.fail(`'${field}' is not an operator code (two to four of a-z, 0-9): '${id}'`);
    return id;
}

function readOrganizationCid(p: StrictObject, field: string): string {
    const cid = p.string(field);
    if (!isOrganizationCid(cid)) throw p.fail(`'${field}' is not an organization CID: '${cid}'`);
    return cid;
}

function readFromTo(p: StrictObject): [string, string] {
    const from = readOperatorId(p, 'from_operator_id');
    const to = readOperatorId(p, 'to_operator_id');
    if (from === to) throw p.fail('a migration must move to a different operator');
    return [from, to];
}

/** Consents are signed by `main_signing_key`, which is always hybrid. */
function requireHybridKey(p: StrictObject, field: string, profile: SigningProfile, key: Uint8Array): void {
    if (!sameProfile(profile, HYBRID_PROFILE)) {
        throw p.fail(`'${field}' must be a ${HYBRID_PROFILE.parameterSet} key; got ${profile.algorithm}/${profile.parameterSet}`);
    }
    const defect = validatePublicKey(profile, key);
    if (defect !== null) throw p.fail(`'${field}': ${defect}`);
}
